import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import { showGlobalToast } from "../ToastProvider";
import { useVerifyApplyMutation } from "../../Store/api/rtk";
const PremiumInput = ({ label, name, formik, multiline = false }) => {
  const hasError = formik?.touched?.[name] && Boolean(formik?.errors?.[name]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          color: hasError ? "#ff1744" : "rgba(255,255,255,0.7)",
          fontSize: 13,
          mb: 1,
          letterSpacing: "0.5px",
          fontWeight: 500,
          transition: "color 0.3s ease",
        }}
      >
        {label}
      </Typography>
      <Box
        component={multiline ? "textarea" : "input"}
        aria-label={label}
        rows={multiline ? 4 : 1}
        name={name}
        value={formik?.values?.[name]}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        sx={{
          width: "100%",
          bgcolor: "#0a0a0a",
          border: `1px solid ${hasError ? "#ff1744" : "rgba(255, 255, 255, 0.1)"}`,
          borderRadius: "12px",
          color: "#fff",
          fontSize: 15,
          fontFamily: "inherit",
          py: 1.8,
          px: 2,
          outline: "none",
          resize: "none",
          transition: "all 0.3s ease",
          boxSizing: "border-box",
          "&:hover:not(:focus)": {
            borderColor: hasError ? "#ff1744" : "rgba(255, 255, 255, 0.2)",
            bgcolor: "#0f0f0f",
          },
          "&:focus": {
            bgcolor: "#000",
            borderColor: hasError ? "#ff1744" : "#00ffcc",
            boxShadow: hasError
              ? "0 0 0 4px rgba(255, 23, 68, 0.1)"
              : "0 0 0 4px rgba(0, 255, 204, 0.1)",
          },
        }}
      />
      {hasError && (
        <Typography sx={{ color: "#ff1744", fontSize: 12, mt: 0.8, pr: 0.5 }}>
          {formik?.errors?.[name]}
        </Typography>
      )}
    </Box>
  );
};
export default function VerificationModal({
  open,
  onClose,
  phoneNumber,
  tmpToken,
  onSuccess,
}) {
  const [verifyApply, { isLoading: isSubmitting }] = useVerifyApplyMutation();

  const formik = useFormik({
    initialValues: {
      verf_code: "",
    },
    validationSchema: yup.object({
      verf_code: yup.string().required("وارد کردن کد تایید الزامی است"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          phone_number: phoneNumber,
          tmp_token: tmpToken,
          verf_code: values?.verf_code,
        };

        await verifyApply(payload).unwrap();

        showGlobalToast("ثبت‌نام با موفقیت تایید شد", "success");
        onSuccess?.();
        onClose?.();
      } catch (error) {
        console.error(error);
        showGlobalToast("کد تایید اشتباه است یا منقضی شده", "error");
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik?.resetForm();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderTop: "1px solid rgba(0, 255, 204, 0.4)",
          borderRadius: "24px",
          color: "#fff",
          direction: "rtl",
          boxShadow: "none",
        },
      }}
    >
      <DialogContent sx={{ p: 4, bgcolor: "transparent" }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <VerifiedUserOutlinedIcon
            sx={{ color: "#00ffcc", fontSize: 48, mb: 1 }}
          />
          <Typography
            sx={{ fontSize: 20, fontWeight: 900, color: "#fff", mb: 1 }}
          >
            تایید شماره موبایل
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
            کد تایید ارسال شده به شماره {phoneNumber} را وارد کنید.
          </Typography>
        </Box>

        <FormikProvider value={formik}>
          <Box component="form" onSubmit={formik?.handleSubmit}>
            <PremiumInput
              formik={formik}
              name="verf_code"
              label="کد تایید"
              placeholder="مثال: 12345"
              // type="number"
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Box
                component="button"
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                sx={{
                  bgcolor: "transparent",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  py: 1.5,
                  px: 3,
                  fontSize: 14,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                انصراف
              </Box>

              <Box
                component="button"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "#00ffcc",
                  color: "#000",
                  border: "none",
                  borderRadius: "12px",
                  py: 1.5,
                  px: 4,
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: isSubmitting ? "none" : "translateY(-2px)",
                    boxShadow: isSubmitting
                      ? "none"
                      : "0 4px 15px rgba(0, 255, 204, 0.3)",
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} sx={{ color: "#000" }} />
                ) : (
                  "تایید نهایی"
                )}
              </Box>
            </Box>
          </Box>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
